import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import NewsletterSignup from "@/components/NewsletterSignup";
import { RULES } from "@/lib/rulebook";

export default function Rulebook() {
  const [filter, setFilter] = useState("");
  const location = useLocation();

  // jump to an anchor (e.g. /rulebook#penalty) after mount
  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (id) {
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const q = filter.trim().toLowerCase();
  const visible = useMemo(
    () =>
      RULES.filter(
        (r) =>
          !q ||
          r.name.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q)
      ),
    [q]
  );

  const jump = (slug) => {
    const el = document.getElementById(slug);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="rulebook-page">
      <Seo
        title="The Rulebook"
        description="Every VoluntoldHQ series explained — Time Out, Penalty, Instant Replay, Crossed Signals, Hot Take Huddle and more."
        path="/rulebook"
      />

      {/* ===== HERO (dark) ===== */}
      <section className="bg-[color:var(--vt-dark)] vt-grain" data-testid="rulebook-hero">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <span className="vt-eyebrow">How VoluntoldHQ Works</span>
          <h1 className="mt-3 font-head text-[color:var(--vt-white)] text-6xl lg:text-[132px] leading-[0.86]">
            <span className="vt-brush">THE RULEBOOK</span>
          </h1>
          <p className="mt-8 text-[color:var(--vt-body-on-dark)] text-lg lg:text-xl max-w-2xl leading-relaxed">
            Every series on VoluntoldHQ, explained. No whistle required.
          </p>
        </div>
        {/* mobile: horizontal scroll index under hero */}
        <div className="lg:hidden border-t border-[color:var(--vt-rule-dark)]">
          <div className="flex gap-3 overflow-x-auto px-5 py-4 snap-x" data-testid="rulebook-index-mobile">
            {RULES.map((r) => (
              <button
                key={r.slug}
                onClick={() => jump(r.slug)}
                data-testid={`rulebook-jump-${r.slug}`}
                className="shrink-0 snap-start font-head uppercase text-sm text-[color:var(--vt-white)] border border-[color:var(--vt-rule-dark)] px-3 py-2 hover:border-[color:var(--vt-amber)] hover:text-[color:var(--vt-amber)] transition-colors"
              >
                <span className="text-[color:var(--vt-amber)] mr-1">{r.number}</span>
                {r.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== RULES (cream, paper) ===== */}
      <section className="vt-paper">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
          {/* sticky desktop index */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <div className="vt-eyebrow vt-eyebrow--cream mb-4">The Rules</div>
              <ul className="space-y-2" data-testid="rulebook-index-desktop">
                {RULES.map((r) => (
                  <li key={r.slug}>
                    <button
                      onClick={() => jump(r.slug)}
                      data-testid={`rulebook-index-${r.slug}`}
                      className="text-left w-full font-[Nunito_Sans] font-bold text-sm text-[color:var(--vt-body-on-cream)] hover:text-[color:var(--vt-amber)] transition-colors flex gap-2"
                    >
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace" }} className="text-[color:var(--vt-label-on-cream)]">
                        {r.number}
                      </span>
                      {r.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* rules list */}
          <div>
            {/* find a rule */}
            <div className="mb-8">
              <label htmlFor="rulebook-filter" className="sr-only">Find a rule</label>
              <input
                id="rulebook-filter"
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Find a rule…"
                data-testid="rulebook-filter"
                className="w-full max-w-md bg-white border-2 border-[color:var(--vt-dark)] px-4 py-3 text-sm text-[color:var(--vt-body-on-cream)] placeholder:text-[#9AA1A6] focus:outline-none focus:border-[color:var(--vt-amber)] transition-colors"
              />
            </div>

            {visible.length === 0 ? (
              <p className="text-[color:var(--vt-body-on-cream)]" data-testid="rulebook-no-match">
                No rule by that name. Try another word.
              </p>
            ) : (
              <div>
                {visible.map((r) => (
                  <article
                    key={r.slug}
                    id={r.slug}
                    data-testid={`rule-${r.slug}`}
                    className="scroll-mt-28 border-t border-[color:var(--vt-dark)]/20 py-9 first:border-t-0"
                  >
                    <span
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                      className="text-[color:var(--vt-amber)] text-sm tracking-[0.18em]"
                    >
                      RULE {r.number}
                    </span>
                    <h2 className="mt-2 font-head text-[color:var(--vt-body-on-cream)] text-[40px] lg:text-[56px] leading-[0.92]">
                      {r.name}
                    </h2>
                    <p className="mt-4 text-[color:var(--vt-body-on-cream)] text-lg leading-relaxed max-w-2xl">
                      {r.description}
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                      <span className="text-sm">
                        <span
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          className="text-[color:var(--vt-label-on-cream)] tracking-[0.18em] mr-2"
                        >
                          WHERE:
                        </span>
                        <span className="text-[color:var(--vt-body-on-cream)] font-semibold">{r.where}</span>
                      </span>
                      {r.link.startsWith("/#") || r.link === "/" ? (
                        <Link
                          to={r.link}
                          data-testid={`rule-cta-${r.slug}`}
                          className="font-head uppercase tracking-wide text-sm text-[color:var(--vt-amber)] hover:text-[color:var(--vt-amber-hover)] transition-colors"
                        >
                          {r.cta}
                        </Link>
                      ) : (
                        <Link
                          to={r.link}
                          data-testid={`rule-cta-${r.slug}`}
                          className="font-head uppercase tracking-wide text-sm text-[color:var(--vt-amber)] hover:text-[color:var(--vt-amber-hover)] transition-colors"
                        >
                          {r.cta}
                        </Link>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== CLOSING (dark) ===== */}
      <section className="bg-[color:var(--vt-dark)] vt-grain" data-testid="rulebook-closing">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <h2 className="font-head text-[color:var(--vt-white)] text-5xl lg:text-7xl leading-[0.9]">
            NOW YOU KNOW <span className="text-[color:var(--vt-amber)]">THE RULES.</span>
          </h2>
          <div className="mt-10 max-w-xl">
            <span className="vt-eyebrow">One read a week. From the sideline.</span>
            <div className="mt-3">
              <NewsletterSignup source="rulebook" tone="dark" showCopy={false} showRole testId="rulebook-newsletter" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
